import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom,
  mergeMap
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { NzNotificationService } from 'ng-zorro-antd/notification';

import * as ChatActions from './chat.actions';
import * as ChatSelector from './chat.selectors';

import { APIService } from '../../services/api/api.service';
import { ChatService } from './chat.service';
import { from, of } from 'rxjs';
import { addMinutes } from 'date-fns';
import * as AppSelectors from '../app/app.selectors';
import { AppState } from '../app.states';
import { DrawerParms, DrawerType } from '@care-portals/api-interfaces';
import { DrawerStateFacade } from '../drawer-state/drawer-state.facade';

@Injectable()
export class ChatEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private apiService: APIService,
    private notification: NzNotificationService,
    private chatService: ChatService,
    private drawerFacade: DrawerStateFacade
  ) {}

  openChat$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatActions.openChat),
        tap(({ style }) => {
          if (style === 'drawer') {
            this.drawerFacade.openDrawer({
              name: DrawerType.CUSTOMER_CHAT,
              props: {}
            } as DrawerParms);
          }
        })
      ),
    { dispatch: false }
  );

  startChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.startChat),
      withLatestFrom(this.store.select(ChatSelector.activeCustomer)),
      mergeMap(([{}, customer]) =>
        this.apiService
          .startChat(customer._id)
          .pipe(map(response => ({ customer, response })))
      ),
      map(({ customer, response }) => {
        if (!response) {
          throw new Error('Chat not found');
        }
        return ChatActions.startChatSuccess({
          customer,
          response
        });
      }),
      catchError(error => {
        this.notification.error(
          'Error',
          'There was an error starting encounter chat.'
        );
        return of(ChatActions.startChatFailure({ error }));
      })
    )
  );
  startEncounterSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.startChatSuccess),
      withLatestFrom(this.store.select(ChatSelector.getConnectionState)),
      map(([{ response }, connectionState]) => {
        if (connectionState === 'connected') {
          return ChatActions.loadMessages();
        } else {
          this.chatService.initClient(response.auth.token); // will call loadMessages when 'connected'
          return ChatActions.nullAction();
        }
      })
    )
  );

  forwardCS$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.forwardCS),
      switchMap(action =>
        this.apiService.forwardEncounter(action.customerId, 'CS').pipe(
          tap(() => {
            this.notification.success('Forward to CS', 'Sent successfully');
          }),
          map(() => ChatActions.forwardCSSuccess()),
          catchError(error => {
            this.notification.error('Forward to CS', 'Failed');
            console.error('ERROR', error);
            return of(ChatActions.forwardCSFailure({ error }));
          })
        )
      )
    )
  );

  forwardPH$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.forwardPH),
      switchMap(action =>
        this.apiService.forwardEncounter(action.customerId, 'PH').pipe(
          tap(() => {
            this.notification.success('Forward to Pharmacy', 'Sent successfully');
          }),
          map(() => ChatActions.forwardPHSuccess()),
          catchError(error => {
            this.notification.error('Forward to Pharmacy', 'Failed');
            console.error('ERROR', error);
            return of(ChatActions.forwardPHFailure({ error }));
          })
        )
      )
    )
  );

  sendPing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.ping),
      withLatestFrom(this.store.select(AppSelectors.getSelectedLayout)),
      switchMap(([action, selectedLayout]) =>
        this.apiService
          .sendMessage({
            channel: 'SMS',
            content:
              selectedLayout.defaultPingMsg ||
              `🛎️ Ping! ${selectedLayout.title} care team is waiting for your response. Please go to your patient portal to respond.`,
            customerId: action.customerId
          })
          .pipe(
            tap(() => {
              this.notification.success('Ping', 'Sent successfully');
            }),
            map(() => ChatActions.pingSuccess()),
            catchError(error => {
              this.notification.error('Ping', 'Failed to send');
              console.error('ERROR', error);
              return of(ChatActions.pingFailure({ error }));
            })
          )
      )
    )
  );

  refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.refreshToken),
      withLatestFrom(this.store.select(ChatSelector.getAuth)),
      switchMap(([action, auth]) =>
        this.apiService.refreshToken(auth.token).pipe(
          map(auth => ChatActions.refreshTokenSuccess({ auth })),
          catchError(error => {
            console.error('ERROR', error);
            return of(ChatActions.refreshTokenFailure({ error }));
          })
        )
      )
    )
  );

  refreshTokenSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatActions.refreshTokenSuccess),
        switchMap(({ auth }) => this.chatService.setNewToken(auth.token))
      ),
    { dispatch: false }
  );

  connectionStateChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.connectionStateChanged),
      withLatestFrom(this.store.select(ChatSelector.getAuth)),
      map(([{ connectionState }, auth]) => {
        if (connectionState === 'disconnecting' && auth) {
          return ChatActions.refreshToken();
        } else if (connectionState === 'connected') {
          return ChatActions.loadMessages();
        } else {
          return ChatActions.nullAction();
        }
      })
    )
  );

  loadMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.loadMessages),
      withLatestFrom(this.store.select(ChatSelector.getActiveId)),
      mergeMap(([action, chatId]) =>
        from(this.chatService.loadMessages(chatId)).pipe(
          map(messages => {
            return ChatActions.loadMessagesSuccess({ chatId, messages });
          })
        )
      )
    )
  );

  loadMore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.loadMore),
      withLatestFrom(this.store.select(ChatSelector.getMessages)),
      mergeMap(([action, currentMessages]) =>
        from(
          this.chatService.loadMessages(action.chatId, {
            anchor: currentMessages.length
          })
        ).pipe(
          map(messages => {
            return ChatActions.loadMoreSuccess({
              chatId: action.chatId,
              messages
            });
          })
        )
      )
    )
  );

  removeParticipant$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatActions.removeParticipant),
        withLatestFrom(this.store.select(ChatSelector.getChatState)),
        tap(([action, state]) => {
          // Dropped when a superseded view tears down late: removing our
          // identity would kick us out of the conversation that replaced it.
          if (action.sessionId != null && action.sessionId !== state.sessionId) {
            return;
          }
          //there is no selectedId and identity for JJC
          if (state.selectedId && state.identity) {
            this.chatService.removeParticipant(
              state.identity,
              state.selectedId
            );
          }
        })
      ),
    { dispatch: false }
  );

  closeChat$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatActions.closeChat),
        withLatestFrom(this.store.select(ChatSelector.getChatState)),
        tap(([action, state]) => {
          if (action.sessionId != null && action.sessionId !== state.sessionId) {
            return;
          }
          this.chatService.closeConnection();
        })
      ),
    { dispatch: false }
  );

  sendMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatActions.sendMessage),
        withLatestFrom(this.store.select(ChatSelector.getChatEntities)),
        map(([action, entities]) => ({
          convo: entities[action.chatId],
          message: action.message,
          files: action.files
        })),
        switchMap(({ convo, message, files }) =>
          this.chatService.sendMessage(convo.id, message, files)
        )
      ),
    { dispatch: false }
  );

  joinVideo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.joinVideo),
      switchMap(({ customerId }) =>
        this.apiService.joinVideo(customerId).pipe(
          map(res => {
            return ChatActions.joinVideoSuccess({
              hostUrl: res.hostUrl,
              joinUrl: res.joinUrl
            });
          }),
          catchError(error => of(ChatActions.joinVideoFailure({ error })))
        )
      )
    )
  );

  joinVideoSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatActions.joinVideoSuccess),
        withLatestFrom(this.store.select(ChatSelector.getActiveId)),
        tap(([{ hostUrl, joinUrl }, chatId]) => {
          window.open(
            hostUrl,
            'CarePortals Video Session',
            'fullscreen=yes,titlebar=no,toolbar=no,menubar=no'
          );
          const msg = {
            type: 'videoInvite',
            expiry: Date.parse(addMinutes(new Date(), 30).toString()),
            joinUrl
          };
          this.chatService.sendMessage(chatId, 'JSON::' + JSON.stringify(msg));
        })
      ),
    { dispatch: false }
  );

  joinVideoFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatActions.joinVideoFailure),
        tap(({ error }) => {
          this.notification.error(
            'Failed to join video session',
            error.message
          );
          console.error(error);
        })
      ),
    { dispatch: false }
  );
}
